import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiResponse,
  Note,
  NotePayload,
  PagedResponse,
  Feature,
} from './notes';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment.development';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class NoteService {
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly BASE_URL = environment.apiUrl;
  private readonly ORGANIZATION_NOTE = `${this.BASE_URL}/OrganizationNote`;

  private notesSubject = new BehaviorSubject<Note[]>([]);
  private noteFeaturesSubject = new BehaviorSubject<Feature[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private currentPageSubject = new BehaviorSubject<number>(1);
  private totalRowsSubject = new BehaviorSubject<number>(0);

  public notes$ = this.notesSubject.asObservable();
  public noteFeatures$ = this.noteFeaturesSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public currentPage$ = this.currentPageSubject.asObservable();
  public totalRows$ = this.totalRowsSubject.asObservable();

  constructor(private http: HttpClient) {}

  loadNoteFeatures(): void {
    this.loadingSubject.next(true);
    this.http
      .get<ApiResponse<Feature[]>>(
        `${this.ORGANIZATION_NOTE}/get-notes-features`
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)))
      .subscribe({
        next: response => {
          this.noteFeaturesSubject.next(response.result);
          this.loadingSubject.next(false);
        },
        error: () => {
          console.error('Failed to load note features');
          this.loadingSubject.next(false);
        },
      });
  }

  loadNotes(params: {
    pageSize?: number;
    page?: number;
    sort?: string;
    sortOrder?: number;
    searchTerm?: string;
    organizationId: number;
  }): void {
    const {
      pageSize = 10,
      page = 1,
      sort = '',
      sortOrder = '',
      searchTerm = '',
      organizationId,
    } = params;

    this.loadingSubject.next(true);
    this.currentPageSubject.next(page);

    let queryParams = `PageSize=${pageSize}&Page=${page}`;

    if (sort) {
      queryParams += `&Sort=${sort}`;
    }

    if (sortOrder) {
      queryParams += `&SortOrder=${sortOrder}`;
    }

    if (searchTerm) {
      queryParams += `&SearchTerm=${searchTerm}`;
    }

    if (organizationId) {
      queryParams += `&OrganizationId=${organizationId}`;
    }

    this.http
      .get<ApiResponse<PagedResponse<Note>>>(
        `${this.ORGANIZATION_NOTE}/list?${queryParams}`
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)))
      .subscribe({
        next: response => {
          this.notesSubject.next(response.result.items);
          this.totalRowsSubject.next(response.result.totalRows);
          this.loadingSubject.next(false);
        },
        error: () => {
          console.error('Failed to load notes');
          this.loadingSubject.next(false);
        },
      });
  }

  getNoteFeatures(): Observable<ApiResponse<Feature[]>> {
    return this.http
      .get<
        ApiResponse<Feature[]>
      >(`${this.ORGANIZATION_NOTE}/get-notes-features`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  createNote(note: NotePayload): Observable<ApiResponse<Note>> {
    this.loadingSubject.next(true);
    return this.http
      .post<ApiResponse<Note>>(`${this.ORGANIZATION_NOTE}/create`, note)
      .pipe(
        tap(() => this.refreshNotes(note.organizationId)),
        catchError(error => {
          this.loadingSubject.next(false);
          return this.errorHandler.handleError(error);
        })
      );
  }

  updateNote(id: number, note: NotePayload): Observable<ApiResponse<Note>> {
    this.loadingSubject.next(true);
    return this.http
      .put<ApiResponse<Note>>(`${this.ORGANIZATION_NOTE}/update`, note, {
        params: { id: id.toString() },
      })
      .pipe(
        tap(() => this.refreshNotes(note.organizationId)),
        catchError(error => {
          this.loadingSubject.next(false);
          return this.errorHandler.handleError(error);
        })
      );
  }

  refreshNotes(organizationId: number): void {
    const currentPage = this.currentPageSubject.getValue();
    this.loadNotes({
      page: currentPage,
      organizationId: organizationId,
    });
  }
}
